import Alert, { ActionOptions, IconOptions } from '../../../../components/common/alert/alert'
import { HomeContext, TemplateIdentity } from '../../state/home-context'
import { useContext } from 'react'
import { AlertMessage, useApiGetAlert } from '../../../../core/api/home/useApiGetAlert'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { getPathFromRouteId } from '../../../../core/utils/route-utils'
import { RouteId } from '../../../../core/routing/router/route-ids'
import { isHomeApiEnabled } from 'core/api/home/home-api-utils'
import { HOMEPAGE_EVENTS } from 'core/constants/homepage-analytics'
import { useHomepageAnalytics } from '../../analytics/use-homepage-analytics'
import { TEMPLATE_NAME, TemplateName } from '../../../../core/utils/templates'
import { useFormatNumber } from '../../../../core/hooks/useFormatNumber'

const getTemplate = (templates: TemplateIdentity[], templateName: TemplateName) => {
  return templates?.find(template => template.label.startsWith(templateName)) ?? ''
}

const isAlertCodeValid = (alert: AlertMessage, aggregation: 'STORE' | 'TEAM'): boolean => {
  if (aggregation === 'STORE') {
    return ['NO_TRAJECTORY_YET', 'GMV_FORECAST_OUT_OF_DATE', 'SIGNIFICANT_GMV_HOURS_GAP'].includes(alert.code)
  } else if (aggregation === 'TEAM') {
    return [
      'NO_TRAJECTORY_YET',
      'HOURS_FORECAST_OUT_OF_DATE',
      'SIGNIFICANT_GMV_HOURS_GAP',
      'GMV_FORECAST_OUT_OF_DATE'
    ].includes(alert.code)
  }
  return false
}

const getBannerLabel = (alert: AlertMessage | null, aggregation: 'STORE' | 'TEAM'): string | null => {
  if (alert?.code === 'NO_TRAJECTORY_YET') return 'general.homepage.alert.no-trajectory'
  if (alert?.code === 'HOURS_FORECAST_OUT_OF_DATE') return 'general.homepage.alert.hours-forecast-out-of-date'
  if (alert?.code === 'SIGNIFICANT_GMV_HOURS_GAP') {
    return 'general.homepage.alert.significant-gmv-hours-gap'
  }
  if (alert?.code === 'GMV_FORECAST_OUT_OF_DATE') {
    return aggregation === 'STORE'
      ? 'general.homepage.alert.gmv-forecast-out-of-date-store'
      : 'general.homepage.alert.gmv-forecast-out-of-date-team'
  }

  return null
}

const getButtonLabel = (alert: AlertMessage | null): string | null => {
  if (alert?.code === 'NO_TRAJECTORY_YET') return 'general.homepage.alert.no-trajectory-button'
  if (alert?.code === 'HOURS_FORECAST_OUT_OF_DATE') return 'general.homepage.hours-forecast-out-of-date-button'
  if (alert?.code === 'GMV_FORECAST_OUT_OF_DATE' || alert?.code === 'SIGNIFICANT_GMV_HOURS_GAP')
    return 'general.homepage.alert.edit-my-forecast-button'
  return null
}

const getStoreForecastPath = (storeId: number, templates: TemplateIdentity[]): string => {
  const params = new URLSearchParams({ store_id: storeId.toString() })
  params.append('year', new Date().getFullYear().toString())

  const template = getTemplate(templates, TEMPLATE_NAME.INSTORE_PERFORMANCE)
  if (template) params.append('channel', template.id.toString())

  return `${getPathFromRouteId(RouteId.DailyStore)}?${params.toString()}`
}

const buildBaseTeamParams = (storeId: number, teamId: number): URLSearchParams => {
  const params = new URLSearchParams({ store_id: storeId.toString() })
  if (teamId) params.append('team', teamId.toString())
  return params
}

const getTeamTrajectoryPath = (storeId: number, teamId: number, templates: TemplateIdentity[]): string => {
  const params = buildBaseTeamParams(storeId, teamId)
  const template = getTemplate(templates, TEMPLATE_NAME.DEBRIEF_DEPARTMENT_MANAGER)
  if (template) params.append('template', template.id.toString())

  return `${getPathFromRouteId(RouteId.WeeklyTeams)}?${params.toString()}`
}

const getTeamForecastPath = (storeId: number, teamId: number, templates: TemplateIdentity[]): string => {
  const params = buildBaseTeamParams(storeId, teamId)
  params.append('year', new Date().getFullYear().toString())

  const template = getTemplate(templates, TEMPLATE_NAME.INSTORE_PERFORMANCE)
  if (template) params.append('channel', template.id.toString())

  return `${getPathFromRouteId(RouteId.DailyTeam)}?${params.toString()}`
}

const getTeamHoursPath = (storeId: number, teamId: number, teamType: string): string => {
  const params = new URLSearchParams({ store_id: storeId.toString() })
  params.append('year', new Date().getFullYear().toString())

  if (teamType === 'GENERAL_SERVICES') return `${getPathFromRouteId(RouteId.HoursGeneralServices)}?${params.toString()}`
  if (teamType === 'RECEPTION_AND_TILL')
    return `${getPathFromRouteId(RouteId.HoursCashierServices)}?${params.toString()}`

  if (teamId) params.append('team', teamId.toString())
  return `${getPathFromRouteId(RouteId.HoursTeam)}?${params.toString()}`
}

const getStoreRedirect = (alertCode: string, storeId: number, templates: TemplateIdentity[]): string | null => {
  if (alertCode === 'NO_TRAJECTORY_YET') return getPathFromRouteId(RouteId.WeeklyStores)
  if (alertCode === 'GMV_FORECAST_OUT_OF_DATE' || alertCode === 'SIGNIFICANT_GMV_HOURS_GAP')
    return getStoreForecastPath(storeId, templates)
  return null
}

const getTeamRedirect = (
  alertCode: string,
  storeId: number,
  teamId: number,
  teamType: string,
  templates: TemplateIdentity[]
): string | null => {
  if (alertCode === 'NO_TRAJECTORY_YET') return getTeamTrajectoryPath(storeId, teamId, templates)
  if (alertCode === 'HOURS_FORECAST_OUT_OF_DATE') return getTeamHoursPath(storeId, teamId, teamType)
  if (alertCode === 'GMV_FORECAST_OUT_OF_DATE' || alertCode === 'SIGNIFICANT_GMV_HOURS_GAP')
    return getTeamForecastPath(storeId, teamId, templates)
  return null
}

const getRedirectUriPath = (
  alert: AlertMessage | null,
  aggregation: 'STORE' | 'TEAM',
  storeId: number,
  teamId: number,
  teamType: string,
  templates: TemplateIdentity[]
): string | null => {
  const alertCode = alert?.code
  if (!alertCode) return null

  if (aggregation === 'STORE') return getStoreRedirect(alertCode, storeId, templates)
  if (aggregation === 'TEAM') return getTeamRedirect(alertCode, storeId, teamId, teamType, templates)
  return null
}

const formatGap = (
  gapValue: string,
  formatNumber: (value: number | null | undefined, fallback?: string, options?: Intl.NumberFormatOptions) => string
) => {
  return gapValue && !Number.isNaN(gapValue)
    ? formatNumber(Number.parseFloat(gapValue), '', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      })
    : ''
}
const AlertBanner = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const formatNumber = useFormatNumber()
  const trackHomepageEvent = useHomepageAnalytics()
  const { aggregation, channel, storeId, teamId, team, templates } = useContext(HomeContext)
  const { data: alert } = useApiGetAlert(
    { aggregation, channel, storeId, teamId },
    isHomeApiEnabled(storeId, teamId, aggregation)
  )

  const hasAlert = alert?.code != null && alert.code !== '' && isAlertCodeValid(alert, aggregation)

  const iconOptions: IconOptions = {
    name: 'warning-error',
    style: 'FILL'
  }

  const bannerLabel = getBannerLabel(alert, aggregation)
  const buttonLabel = getButtonLabel(alert)
  const actionFunction = getRedirectUriPath(alert, aggregation, storeId, teamId, team?.team_type ?? '', templates)
  const actionOptions: ActionOptions = actionFunction
    ? {
        label: buttonLabel ? t(buttonLabel) : '',
        doOnClick: () => {
          trackHomepageEvent(HOMEPAGE_EVENTS.CLICK_ALERT_ACTION, { alert_code: alert?.code })
          void navigate(actionFunction)
        }
      }
    : undefined

  return (
    <>
      {hasAlert ? (
        <Alert
          text={
            bannerLabel
              ? t(bannerLabel, {
                  gap: formatGap(alert?.value, formatNumber)
                })
              : ''
          }
          icon={iconOptions}
          severity="warning"
          action={actionOptions}
          closable={{
            onClose: () => {
              trackHomepageEvent(HOMEPAGE_EVENTS.CLICK_ALERT_DISMISS, { alert_code: alert?.code })
            }
          }}
        />
      ) : null}
    </>
  )
}

export default AlertBanner
